import Config from 'config/settings';
import { globals } from 'config/globals';
import { ParticleBehaviourTagType } from 'app/enum/Particles';
import { ParticleBehaviour } from '../ParticleBehaviour';
import { HomeParticle } from 'app/views/home/webgl/particles/fields/HomeParticle';
import ParticlePool from 'app/views/home/webgl/particles/fields/ParticlePool';
import ParticleGridTargets from 'app/views/home/webgl/particles/targets/ParticleGridTargets';
import { device } from 'utils/device';

export class ParticleTrailEmitter extends ParticleBehaviour {
	protected _tags: Array<ParticleBehaviourTagType> = [ParticleBehaviourTagType.TRAIL];

	private _spawnFrames: number = 1;
	private _spawnFramesCount: number = 0;
	private _lifetimeFrames: number = -1;
	private _childLifetimeFramesBase: number = 50;
	private _childLifetimeFramesSeed: number = 0;
	private _onLifetimeEnded: Function;
	private _alpha = 1;
	private _alphaStep;
	private _scale;
	private _scaleStep;
	private _generateScale;

	private _mode = 'tick'; //distance or tick. Tick will just place on frames, distance will evenly space based on movement
	private pointAtEachDistance = 0.001;
	private lastTrailPoint;
	private _colour = '#ffffff';

	//GETTER / SETTER
	//--------------------------------------------------------------------------------------------------
	public set spawnFrames(frames: number) {
		this._spawnFrames = frames;
		this._spawnFramesCount = 0;
	}

	public set alpha(v: number) {
		this._alpha = v;
		this._setAlphaStep();
	}

	public set scale(v: number) {
		this._scale = v;
		this._setScaleStep();
	}

	public startDistanceMode(startX, startY) {
		this._mode = 'distance';
		this.lastTrailPoint = {
			x: startX,
			y: startY
		};
	}

	public setColour(colour) {
		this._colour = colour;
	}

	public randomiseLifetime(base: number, seed: number = 0): void {
		this._lifetimeFrames = Math.round(base + Math.random() * seed);
		this._setAlphaStep();
		this._setScaleStep();
	}

	public randomiseSpawnFrames(base: number, seed: number = 0): void {
		this._spawnFrames = Math.round(base + Math.random() * seed);
		this._spawnFramesCount = 0;
	}

	public randomiseChildLifetime(base: number, seed: number = 0): void {
		this._childLifetimeFramesBase = base;
		this._childLifetimeFramesSeed = seed;

		let mobileFix = 2.7;
		let mobileScaleFix = 1.3;
		if (Math.random() < 0.2) {
			this.pointAtEachDistance = Math.random() < 0.4 ? 0.004 : 0.002;
			this._generateScale = this.pointAtEachDistance == 0.004 ? 0.9 : 0.65;

			mobileFix = 1.2;
			mobileScaleFix = 1.2;
		} else {
			this.pointAtEachDistance = 0.0008;
			this._generateScale = 0.4;
		}

		if (device.innerWidth < Config.mobileBreakpoint) {
			this.pointAtEachDistance *= mobileFix;
			this._generateScale *= mobileScaleFix;
		}
	}

	public clear(): void {}

	//PUBLIC METHODS
	//-----------------------------------------------------------------------------------------
	public reset(): void {
		this._lifetimeFrames = -1;
		this._spawnFramesCount = 0;
	}

	public burst(count: number, xSide: number): void {
		for (let i = 0; i < count; i++) {
			let particle: HomeParticle = ParticlePool.get('systems');

			(particle as HomeParticle).activateAsTrail(this._particleProps, this._animatedProps, 40, 60);
			let ySide = Math.random() < 0.5 ? -1 : 1;
			particle.animatedProps.targetX += xSide * -1 * (30 + Math.random() * 70);
			particle.animatedProps.targetY += ySide * (Math.random() * 30);
			particle.animatedProps.setEase({ x: 40, y: 20 });
		}
	}

	public tick(): void {
		const xOffset = this._sceneProps.mouse.particleContainersX['systems'];
		const inSafeZone = ParticleGridTargets.inSafeZone(
			xOffset + this._particleProps.ref.position.absX,
			this._particleProps.ref.position.absY - globals.yGrid
		);

		const yRatio = this._sceneProps.sizes.container.h / this._sceneProps.sizes.container.w;

		if (this._mode == 'distance') {
			//Draw a vector evenly from the start position
			let currentPoint = { x: this._particleProps.ref.position.x, y: this._particleProps.ref.position.y };
			let distance = Math.hypot(
				currentPoint.x - this.lastTrailPoint.x,
				currentPoint.y * yRatio - this.lastTrailPoint.y * yRatio
			);

			//If current pos is now far away enough from last point then should be able to add at least 1 point to trail
			if (Math.abs(distance) > this.pointAtEachDistance) {
				//Work out relative X/Y distance:
				const xDiff = currentPoint.x - this.lastTrailPoint.x;
				const yDiff = currentPoint.y - this.lastTrailPoint.y;
				const xDiffRatio = xDiff / distance;
				const yDiffRatio = yDiff / distance;
				const xStep = this.pointAtEachDistance * xDiffRatio;
				const yStep = this.pointAtEachDistance * yDiffRatio;

				//Start from 0 distance and add dots from nearest to previous pos outwards
				let trackDistance = 0;
				let x = this.lastTrailPoint.x;
				let y = this.lastTrailPoint.y;

				///Loop through and space out as many points as can:

				while (Math.abs(distance) - trackDistance >= this.pointAtEachDistance) {
					let particle: HomeParticle = ParticlePool.get('systems');

					x += xStep;
					y += yStep;

					if (particle) {
						(particle as HomeParticle).activateAsTrail(
							this._particleProps,
							this._animatedProps,
							this._childLifetimeFramesBase,
							this._childLifetimeFramesSeed,
							{ x: x, y: y },
							inSafeZone,
							this._generateScale,
							this._colour
						);
					}

					trackDistance += this.pointAtEachDistance;
				}

				//Last point. Now the furfest away:
				this.lastTrailPoint = {
					x: x,
					y: y
				};
			}
		} else {
			this._spawnFramesCount++;

			if (this._spawnFramesCount >= this._spawnFrames) {
				let particle: HomeParticle = ParticlePool.get('systems');

				if (particle) {
					(particle as HomeParticle).activateAsTrail(
						this._particleProps,
						this._animatedProps,
						this._childLifetimeFramesBase,
						this._childLifetimeFramesSeed
					);
				}
				//Reset the counter:
				this._spawnFramesCount = 0;
			}
		}

		if (this._lifetimeFrames !== -1) {
			this._lifetimeFrames -= (window as any).deltaTime;

			this._alpha -= this._alphaStep;
			this._scaleStep -= this._scaleStep;

			if (this._alpha <= 0) {
				this._alpha = 0;
				this.active = false;
			}

			this._animatedProps.alpha = this._animatedProps.targetAlpha = this._alpha;
			this._animatedProps.scale = this._animatedProps.targetScale = this._scale;

			if (this._lifetimeFrames <= 0) {
				this.active = false;
				if (this._onLifetimeEnded) {
					this._onLifetimeEnded();
				}
			}
		}
	}

	private _setAlphaStep(): void {
		this._alphaStep = this._alpha / this._lifetimeFrames;
	}

	private _setScaleStep(): void {
		this._scaleStep = this._scale / this._lifetimeFrames;
	}
}
